import { Input } from '@/components/ui/Field'
import { cn } from '@/lib/cn'
import { daysAgoInMalaysia, monthRangeInMalaysia, yearStartInMalaysia } from '@/lib/date'

/* Quick ranges, all cut on the Malaysian calendar. Computed per render so a
   page left open overnight still offers the right "today". */
export function presets(): { label: string; from: string; to: string }[] {
  const today = daysAgoInMalaysia(0)
  return [
    { label: 'Today', from: today, to: today },
    { label: '7 days', from: daysAgoInMalaysia(6), to: today },
    { label: '30 days', from: daysAgoInMalaysia(29), to: today },
    { label: 'This month', from: monthRangeInMalaysia(0).from, to: today },
    { label: 'Last month', ...monthRangeInMalaysia(-1) },
    { label: 'This year', from: yearStartInMalaysia(), to: today },
  ]
}

/** The preset pills plus from/to inputs, shared by the admin and sales reports and staff My stats. */
export default function DateRangeBar({
  from,
  to,
  onFrom,
  onTo,
  onPreset,
}: {
  from: string
  to: string
  onFrom: (value: string) => void
  onTo: (value: string) => void
  onPreset: (range: { from: string; to: string }) => void
}) {
  return (
    <div className="flex flex-wrap items-end gap-3">
      {/* One swipeable row on phones, where six wrapped pills take two lines. */}
      <div className="scrollbar-none -my-0.5 flex w-full gap-1.5 overflow-x-auto py-0.5 sm:w-auto sm:flex-wrap sm:overflow-visible">
        {presets().map((preset) => (
          <button
            key={preset.label}
            type="button"
            onClick={() => onPreset(preset)}
            className={cn(
              'shrink-0 whitespace-nowrap rounded-lg border px-2.5 py-2 text-xs font-semibold sm:py-1.5',
              preset.from === from && preset.to === to
                ? 'border-primary/60 bg-primary/20 text-primary'
                : 'border-line text-muted hover:bg-foreground/5',
            )}
          >
            {preset.label}
          </button>
        ))}
      </div>
      {/* Phones get label-left rows so both inputs share one width. Side by
          side, a date plus Chrome's calendar icon clips at 375px. */}
      <div className="grid w-full gap-2 sm:flex sm:w-auto sm:gap-3">
        <label className="grid grid-cols-[2.5rem_minmax(0,1fr)] items-center gap-3 text-sm sm:block">
          <span className="text-xs font-semibold text-muted sm:mb-1 sm:block">From</span>
          <Input
            type="date"
            value={from}
            max={to}
            onChange={(e) => onFrom(e.target.value)}
            className="[&::-webkit-date-and-time-value]:text-left"
          />
        </label>
        <label className="grid grid-cols-[2.5rem_minmax(0,1fr)] items-center gap-3 text-sm sm:block">
          <span className="text-xs font-semibold text-muted sm:mb-1 sm:block">To</span>
          <Input
            type="date"
            value={to}
            min={from}
            onChange={(e) => onTo(e.target.value)}
            className="[&::-webkit-date-and-time-value]:text-left"
          />
        </label>
      </div>
    </div>
  )
}
